/**
 * Activity Report Service
 * Weekly sales activity tracking and AI-powered mentor feedback
 * Analyzes calls, demos, and pitches by industry for personalized coaching
 */

import { supabase } from '../lib/supabase';

const AIMLAPI_KEY = import.meta.env?.VITE_AIMLAPI_KEY;
const AIMLAPI_BASE_URL = 'https://api.aimlapi.com/v1';

const isValidAPIKey = (key) => {
  if (!key) return false;
  const placeholders = ['your-aimlapi-key', 'your_aimlapi_key', 'your_api_key', 'placeholder', 'xxx', 'your-'];
  return !placeholders.some((p) => key.toLowerCase().includes(p));
};

export const activityReportService = {
  /**
   * Submit weekly activity report
   */
  async submitActivityReport(userId, reportData) {
    try {
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('industry, skill_level')
        ?.eq('id', userId)
        ?.single();

      const { data: report, error } = await supabase
        ?.from('user_activity_reports')
        ?.upsert({
          user_id: userId,
          week_starting: reportData.weekStarting,
          calls_completed: reportData.callsCompleted || 0,
          demos_completed: reportData.demosCompleted || 0,
          pitches_completed: reportData.pitchesCompleted || 0,
          key_wins: reportData.keyWins || '',
          challenges_faced: reportData.challengesFaced || '',
          objections_faced: reportData.objectionsFaced || [],
          techniques_applied: reportData.techniquesApplied || [],
          industry: profile?.industry,
          skill_level: profile?.skill_level,
          raw_notes: reportData.rawNotes || ''
        }, { onConflict: 'user_id,week_starting' })
        ?.select()
        ?.single();

      if (error) throw error;

      // Trigger AI analysis
      if (report) {
        const feedback = await this.generateMentorFeedback(userId, report);
        return { ...report, feedback };
      }

      return report;
    } catch (error) {
      console.error('Error submitting activity report:', error);
      throw error;
    }
  },

  /**
   * Generate Lumo mentor feedback on weekly activity
   */
  async generateMentorFeedback(userId, report) {
    let profile;
    try {
      const { data: profileData } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', userId)
        ?.single();
      profile = profileData;

      const systemPrompt = `You are Lumo, a world-class sales mentor analyzing a rep's weekly activity. Provide personalized coaching feedback based on their actual sales work.

SALESPERSON PROFILE:
- Industry: ${profile?.industry || 'General'}
- Skill Level: ${profile?.skill_level || 'Intermediate'}
- Role: ${profile?.role || 'Sales Rep'}

WEEKLY ACTIVITY SUMMARY:
- Calls: ${report.calls_completed}
- Demos: ${report.demos_completed}
- Pitches: ${report.pitches_completed}
- Total Activities: ${report.total_activities}

KEY WINS:
${report.key_wins || 'None reported'}

CHALLENGES FACED:
${report.challenges_faced || 'None reported'}

OBJECTIONS ENCOUNTERED:
${report.objections_faced?.join(', ') || 'None reported'}

TECHNIQUES APPLIED:
${report.techniques_applied?.join(', ') || 'None reported'}

ADDITIONAL NOTES:
${report.raw_notes || 'None'}

ANALYZE AND PROVIDE:
1) What they did exceptionally well this week (be specific)
2) Pattern analysis from their activities (what worked, what didn't)
3) Industry-specific coaching for next week (based on ${profile?.industry || 'their industry'})
4) One specific technique to practice next week
5) A confidence boost message (be genuine and encouraging)

Respond ONLY with valid JSON using this shape:
{
  "strengths": "2-3 specific wins and good decisions",
  "patterns": "What you're doing well and what to build on",
  "industryTips": "Specific advice for ${profile?.industry || 'their industry'} sales",
  "focusTechnique": "One technique to master next week with example",
  "encouragement": "Personal, genuine message about their progress",
  "focusArea": "Primary area to focus on",
  "nextWeekGoal": "Specific, measurable goal",
  "practiceLinks": [
    { "label": "Practice this technique", "url": "/app/techniques-library" },
    { "label": "Run a focused drill", "url": "/app/daily-drill" },
    { "label": "AI Roleplay this scenario", "url": "/app/ai-roleplay" }
  ],
  "score": 0-100
}`;

      if (!AIMLAPI_KEY || !isValidAPIKey(AIMLAPI_KEY)) {
        throw new Error('AIMLAPI key missing or invalid');
      }

      const response = await fetch(`${AIMLAPI_BASE_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AIMLAPI_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          temperature: 0.6,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: report.raw_notes || 'Weekly activity report submitted.' }
          ]
        })
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`AIMLAPI request failed: ${response.status} ${errText}`);
      }

      const data = await response.json();
      const content = data?.choices?.[0]?.message?.content;
      if (!content) {
        throw new Error('AIMLAPI response missing content');
      }

      const feedback = typeof content === 'string' ? JSON.parse(content) : content;

      await supabase
        ?.from('user_activity_reports')
        ?.update({
          mentor_feedback: feedback,
          feedback_generated_at: new Date().toISOString(),
          is_analyzed: true
        })
        ?.eq('id', report.id);

      return feedback;
    } catch (error) {
      console.error('Error generating mentor feedback with AIMLAPI:', error);

      const totalActivities = (report.calls_completed || 0) + (report.demos_completed || 0) + (report.pitches_completed || 0);
      const objectionCount = report.objections_faced?.length || 0;
      const techniqueCount = report.techniques_applied?.length || 0;

      let score = 65;
      if (totalActivities >= 5) score += 10;
      if (totalActivities >= 8) score += 5;
      if (objectionCount >= 2) score += 5;
      if (techniqueCount >= 2) score += 5;
      if (report.key_wins && report.key_wins.length > 10) score += 5;

      const objectionsList = report.objections_faced?.join(', ') || 'various objections';
      const techniquesList = report.techniques_applied?.join(', ') || 'sales techniques';

      const fallback = {
        strengths: report.key_wins
          ? `Excellent work this week! ${report.key_wins} You completed ${report.calls_completed} calls and ${report.demos_completed} demos. That consistent activity is building real sales skills. You're showing up and executing.`
          : `Great effort this week! You completed ${report.calls_completed} calls and ${report.demos_completed} demos. Consistency is key to improvement, and you're demonstrating that.`,

        patterns: objectionCount > 0
          ? `You're encountering real objections (${objectionsList}) - that's where learning happens. You're applying ${techniquesList} which shows intentional technique work. Keep tracking what works and what doesn't.`
          : `You're actively engaging in sales conversations. Focus on identifying patterns in what buyers say and systematically testing your responses.`,

        industryTips: `For ${profile?.industry || 'your industry'} selling: Lead with discovery before presenting solutions. Ask about their situation, problems, and criteria before pitching. This builds credibility and uncovers real pain points.`,

        focusTechnique: techniqueCount > 0
          ? `Build on what you're learning. This week focus on "Active Listening & Clarification": When a buyer raises an objection, pause and ask a clarifying question before responding. Example: 'When you say that's a concern, what specifically matters most to you about this?'`
          : `Master "Discovery Questions": Before pitching, ask about their situation (Situation), their current approach (Problem), and their ideal outcome (Implication). This builds your credibility.`,

        encouragement: `You're building real sales strength. Every call teaches you something. You've completed ${totalActivities} activities this week - that's commitment. Your willingness to face objections and practice techniques shows you're serious about improvement. Keep this pace and you'll be unstoppable by month-end.`,

        focusArea: objectionCount > 0
          ? `Master objection handling - specifically with ${report.objections_faced?.[0] || 'common objections'}`
          : `Deepen your discovery questioning skills to uncover real buyer needs`,

        nextWeekGoal: totalActivities >= 5
          ? `Complete ${report.calls_completed + 2} calls, handle ${Math.max(3, objectionCount + 1)} objections with your focus technique, and document what works. Track your close rate.`
          : `Increase activity volume to 6+ calls/demos this week. Practice your focus technique in at least 3 conversations and measure which buyers respond best.`,

        practiceLinks: [
          { label: 'Practice this technique', url: '/app/techniques-library' },
          { label: 'Run a focused drill', url: '/app/daily-drill' },
          { label: 'AI Roleplay this scenario', url: '/app/ai-roleplay' }
        ],

        score: Math.min(85, Math.max(65, score))
      };

      try {
        await supabase
          ?.from('user_activity_reports')
          ?.update({
            mentor_feedback: fallback,
            feedback_generated_at: new Date().toISOString(),
            is_analyzed: true
          })
          ?.eq('id', report.id);
      } catch (dbError) {
        console.error('Error saving fallback feedback:', dbError);
      }

      return fallback;
    }
  },

  /**
   * Get user's activity reports
   */
  async getActivityReports(userId, limit = 10) {
    try {
      const { data: reports, error } = await supabase
        ?.from('user_activity_reports')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('week_starting', { ascending: false })
        ?.limit(limit);

      if (error) throw error;
      return reports || [];
    } catch (error) {
      console.error('Error getting activity reports:', error);
      return [];
    }
  },

  /**
   * Get latest activity report with feedback
   */
  async getLatestReport(userId) {
    try {
      const { data: report, error } = await supabase
        ?.from('user_activity_reports')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(1)
        ?.single();

      if (error && error.code !== 'PGRST116') throw error;
      return report || null;
    } catch (error) {
      console.error('Error getting latest report:', error);
      return null;
    }
  },

  /**
   * Generate PDF report for printing
   */
  async generatePrintReport(reportId) {
    try {
      const { data: report, error } = await supabase
        ?.from('user_activity_reports')
        ?.select('*, user_profiles!inner(full_name, industry)')
        ?.eq('id', reportId)
        ?.single();

      if (error) throw error;

      const html = this.generateHTMLReport(report);
      return html;
    } catch (error) {
      console.error('Error generating print report:', error);
      throw error;
    }
  },

  /**
   * Generate HTML for printing
   */
  generateHTMLReport(report) {
    const feedback = report.mentor_feedback || {};
    const date = new Date(report.week_starting).toLocaleDateString();

    return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #333; line-height: 1.6; }
        .container { max-width: 900px; margin: 0 auto; padding: 40px; }
        .header { border-bottom: 3px solid #6366f1; margin-bottom: 30px; padding-bottom: 20px; }
        .header h1 { margin: 0; color: #1f2937; font-size: 28px; }
        .header p { margin: 5px 0 0 0; color: #6b7280; }
        .week-info { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
        .info-box { background: #f3f4f6; padding: 15px; border-radius: 8px; }
        .info-box label { font-weight: 600; color: #374151; font-size: 12px; text-transform: uppercase; }
        .info-box .value { font-size: 24px; font-weight: bold; color: #6366f1; }
        .section { margin-bottom: 30px; }
        .section h2 { font-size: 18px; font-weight: 600; color: #1f2937; margin-bottom: 12px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; }
        .section p { margin: 8px 0; }
        .strengths { background: #f0fdf4; padding: 15px; border-left: 4px solid #22c55e; border-radius: 4px; }
        .focus { background: #fef3c7; padding: 15px; border-left: 4px solid #eab308; border-radius: 4px; }
        .quote { font-style: italic; color: #6b7280; margin: 15px 0; padding: 15px; background: #f9fafb; border-radius: 4px; }
        .score { text-align: center; margin: 20px 0; }
        .score-value { font-size: 48px; font-weight: bold; color: ${
          feedback.score >= 80 ? '#22c55e' : feedback.score >= 70 ? '#f59e0b' : '#ef4444'
        }; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px; }
        @media print { body { margin: 0; padding: 0; } .container { padding: 20px; } }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1>Weekly Mentor Report: ${report.user_profiles?.full_name}</h1>
          <p>Week of ${date} • ${report.user_profiles?.industry} • Generated by Lumo</p>
        </div>

        <div class="week-info">
          <div class="info-box">
            <label>Total Activities</label>
            <div class="value">${report.total_activities}</div>
          </div>
          <div class="info-box">
            <label>Mentor Score</label>
            <div class="value">${feedback.score || 0}/100</div>
          </div>
          <div class="info-box">
            <label>Calls</label>
            <div class="value">${report.calls_completed}</div>
          </div>
          <div class="info-box">
            <label>Demos & Pitches</label>
            <div class="value">${report.demos_completed + report.pitches_completed}</div>
          </div>
        </div>

        <div class="section strengths">
          <h2>✓ What You Did Well</h2>
          <p>${feedback.strengths || 'Great work this week maintaining your activity level.'}</p>
        </div>

        <div class="section">
          <h2>📊 Activity Analysis</h2>
          <p><strong>Patterns & Insights:</strong> ${feedback.patterns || 'You\'re consistently applying your techniques and building momentum.'}</p>
        </div>

        <div class="section">
          <h2>🎯 Industry-Specific Coaching</h2>
          <p>${feedback.industryTips || `For ${report.industry} selling, focus on discovery before solution selling.`}</p>
        </div>

        <div class="section focus">
          <h2>🔥 Next Week's Focus</h2>
          <p><strong>Primary Technique:</strong> ${feedback.focusTechnique || 'Mirror & Pivot technique'}</p>
          <p><strong>Goal:</strong> ${feedback.nextWeekGoal || 'Apply one new technique in 3 customer conversations'}</p>
        </div>

        <div class="section">
          <h2>💪 Your Momentum</h2>
          <div class="quote">"${feedback.encouragement || 'You\'re building great habits. Keep showing up and applying what you learn.'}"</div>
        </div>

        <div class="footer">
          <p>Lumo Mentor • Your AI Sales Coach • www.pitchlab.ai</p>
          <p>This report is confidential and for your personal development</p>
        </div>
      </div>
    </body>
    </html>
    `;
  }
};

export default activityReportService;
